import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Monitor, Keyboard, Cpu, BatteryCharging, Plug, Move, Box, HelpCircle, Camera, Upload, ChevronLeft, ChevronRight, Check, Loader2, X, Phone, Globe, MessageCircle, ShieldAlert, PackageCheck, LogIn, User, Star, Truck, Wrench, Search, ClipboardList, ChevronDown } from 'lucide-react'
import LanguageSwitcher from '../components/LanguageSwitcher'
import { useRecaptcha } from '../hooks/useRecaptcha'

const API_BASE = '/api'

const REPAIR_TYPE_CODES = [
  { code: 'screen', icon: Monitor },
  { code: 'keyboard', icon: Keyboard },
  { code: 'motherboard', icon: Cpu },
  { code: 'battery', icon: BatteryCharging },
  { code: 'power', icon: Plug },
  { code: 'hinge', icon: Move },
  { code: 'casing', icon: Box },
  { code: 'general', icon: HelpCircle },
]

const BRANDS = ['Acer', 'Apple', 'ASUS', 'Dell', 'HP', 'Huawei', 'Lenovo', 'LG', 'Microsoft', 'MSI', 'Razer', 'Samsung', 'Toshiba', 'Xiaomi']

const ACCESSORIES = [
  { value: 'Cargador', key: 'repairs.step6.charger' },
  { value: 'Funda', key: 'repairs.step6.case' },
  { value: 'Ratón', key: 'repairs.step6.mouse' },
  { value: 'Cable HDMI', key: 'repairs.step6.hdmiCable' },
  { value: 'Disco externo', key: 'repairs.step6.externalDrive' },
]

export default function PublicRepairRequestPage() {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const [searchParams] = useSearchParams()
  const { executeRecaptcha } = useRecaptcha()

  const [step, setStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [estimate, setEstimate] = useState<any>(null)
  const [diagnosticFee, setDiagnosticFee] = useState(29.90)
  const [result, setResult] = useState<any>(null)
  const [photos, setPhotos] = useState<File[]>([])
  const [recentRepairs, setRecentRepairs] = useState<any[]>([])
  const [priceIncludesVat, setPriceIncludesVat] = useState(true)

  // Customer auth
  const [customer, setCustomer] = useState<any>(null)
  const [customerToken, setCustomerToken] = useState<string | null>(sessionStorage.getItem('jvs_repair_customer_token'))
  const [showLogin, setShowLogin] = useState(false)
  const [loginEmail, setLoginEmail] = useState('')
  const [loginPassword, setLoginPassword] = useState('')
  const [loginError, setLoginError] = useState('')
  const [loginLoading, setLoginLoading] = useState(false)

  // Promo code
  const [promoCode, setPromoCode] = useState(() => searchParams.get('promo') || '')
  const [promoValid, setPromoValid] = useState<any>(null)
  const [promoError, setPromoError] = useState('')
  const [promoChecking, setPromoChecking] = useState(false)

  // Analytics
  const [sessionId] = useState(() => crypto.randomUUID())
  const [source] = useState(() => searchParams.get('source') || 'direct')

  const [form, setForm] = useState({
    repair_type: '',
    device_brand: '',
    device_brand_other: '',
    device_model: '',
    device_serial: '',
    problem_description: '',
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_nif: '',
    street: '',
    city: '',
    postal_code: '',
    province: '',
    country_code: 'ES',
    preferred_date: '',
    accessories: [] as string[],
    accessories_notes: '',
    consent_data: false,
    consent_terms: false,
    consent_diagnostic: false,
    consent_packaging: false,
  })

  const update = (field: string, value: any) => setForm({ ...form, [field]: value })

  // Detect customer_type from URL or customer data
  const customerType = useMemo(() => {
    if (customer?.customer_type) return customer.customer_type
    return searchParams.get('customer_type') || 'PVP'
  }, [customer, searchParams])

  // Pre-fill from customer data
  const prefillFromCustomer = useCallback((c: any) => {
    setForm(f => ({
      ...f,
      customer_name: `${c.firstname || ''} ${c.lastname || ''}`.trim() || f.customer_name,
      customer_email: c.email || f.customer_email,
      customer_phone: c.phone || f.customer_phone,
      customer_nif: c.nif || f.customer_nif,
      street: c.address?.street || f.street,
      city: c.address?.city || f.city,
      postal_code: c.address?.postal_code || f.postal_code,
      province: c.address?.province || f.province,
      country_code: c.address?.country_code || f.country_code,
    }))
  }, [])

  // SSO token exchange on mount
  useEffect(() => {
    const ssoToken = searchParams.get('sso_token')
    if (ssoToken) {
      fetch(`${API_BASE}/repairs/auth/sso`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: ssoToken }),
      })
        .then(r => r.json())
        .then(data => {
          if (data.success) {
            sessionStorage.setItem('jvs_repair_customer_token', data.token)
            setCustomerToken(data.token)
            setCustomer(data.customer)
            prefillFromCustomer(data.customer)
          }
        })
        .catch(() => {})
      // Clean URL
      const newParams = new URLSearchParams(searchParams)
      newParams.delete('sso_token')
      window.history.replaceState({}, '', `${window.location.pathname}${newParams.toString() ? '?' + newParams.toString() : ''}`)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Restore customer session
  useEffect(() => {
    if (customerToken && !customer) {
      fetch(`${API_BASE}/repairs/auth/me`, {
        headers: { 'Authorization': `Bearer ${customerToken}` },
      })
        .then(r => { if (!r.ok) throw new Error(); return r.json() })
        .then(data => {
          setCustomer(data)
          prefillFromCustomer(data)
        })
        .catch(() => {
          sessionStorage.removeItem('jvs_repair_customer_token')
          setCustomerToken(null)
        })
    }
  }, [customerToken]) // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-fill from URL params
  useEffect(() => {
    const type = searchParams.get('type')
    const brand = searchParams.get('brand')
    if (type) setForm(f => ({ ...f, repair_type: type }))
    if (brand) setForm(f => ({ ...f, device_brand: brand }))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Load types info
  useEffect(() => {
    fetch(`${API_BASE}/repairs/types`)
      .then(r => r.json())
      .then(data => {
        if (data.diagnostic_fee) setDiagnosticFee(data.diagnostic_fee)
      })
      .catch(() => {})
  }, [])

  // Fetch recent repairs
  useEffect(() => {
    fetch(`${API_BASE}/repairs/recent-completed?customer_type=${customerType}`)
      .then(r => r.json())
      .then(data => {
        setRecentRepairs(data.repairs || [])
        setPriceIncludesVat(data.price_includes_vat !== false)
      })
      .catch(() => {})
  }, [customerType])

  // Track step changes
  useEffect(() => {
    fetch(`${API_BASE}/repairs/form-analytics`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id: sessionId,
        step,
        repair_type: form.repair_type || null,
        device_brand: form.device_brand || null,
        source,
      }),
    }).catch(() => {})
  }, [step]) // eslint-disable-line react-hooks/exhaustive-deps

  // Validate promo code from URL on mount
  useEffect(() => {
    const promo = searchParams.get('promo')
    if (promo) validatePromo(promo)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const validatePromo = (code: string) => {
    if (!code.trim()) { setPromoValid(null); setPromoError(''); return }
    setPromoChecking(true)
    setPromoError('')
    fetch(`${API_BASE}/repairs/promo/validate?code=${encodeURIComponent(code.trim())}&email=${encodeURIComponent(form.customer_email)}`)
      .then(r => r.json())
      .then(data => {
        if (data.valid) {
          setPromoValid(data.promo)
          setPromoError('')
        } else {
          setPromoValid(null)
          setPromoError(data.error || t('repairs.promo.invalid'))
        }
      })
      .catch(() => { setPromoValid(null); setPromoError(t('repairs.promo.invalid')) })
      .finally(() => setPromoChecking(false))
  }

  // Fetch estimate when type is selected
  useEffect(() => {
    if (form.repair_type) {
      const promoParam = promoValid ? `&promo_code=${encodeURIComponent(promoValid.code)}` : ''
      fetch(`${API_BASE}/repairs/estimate?repair_type=${form.repair_type}&brand=${form.device_brand}&customer_type=${customerType}${promoParam}`, { method: 'POST' })
        .then(r => r.json())
        .then(data => {
          setEstimate(data)
          if (data.diagnostic_fee !== undefined) setDiagnosticFee(data.diagnostic_fee)
        })
        .catch(() => {})
    }
  }, [form.repair_type, form.device_brand, customerType, promoValid])

  // Customer login handler
  const handleLogin = async () => {
    setLoginLoading(true)
    setLoginError('')
    try {
      const res = await fetch(`${API_BASE}/repairs/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password: loginPassword }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.detail || 'Error')
      sessionStorage.setItem('jvs_repair_customer_token', data.token)
      setCustomerToken(data.token)
      setCustomer(data.customer)
      prefillFromCustomer(data.customer)
      setShowLogin(false)
    } catch (err: any) {
      setLoginError(err.message || t('repairs.auth.loginError'))
    } finally {
      setLoginLoading(false)
    }
  }

  const handlePhotoAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    setPhotos(prev => [...prev, ...files].slice(0, 5))
  }

  const removePhoto = (idx: number) => {
    setPhotos(prev => prev.filter((_, i) => i !== idx))
  }

  const submit = async () => {
    setLoading(true)
    try {
      // Normalizar CP portugues: 1000001 -> 1000-001
      let postalCode = form.postal_code.trim()
      if (form.country_code === 'PT' && /^\d{7}$/.test(postalCode)) {
        postalCode = postalCode.slice(0, 4) + '-' + postalCode.slice(4)
      }
      const pickup_address = form.street ? {
        street: form.street, city: form.city, postal_code: postalCode,
        province: form.province, country_code: form.country_code,
      } : undefined

      const recaptchaToken = await executeRecaptcha('repair_request')
      const res = await fetch(`${API_BASE}/repairs/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(customerToken ? { 'Authorization': `Bearer ${customerToken}` } : {}),
          ...(recaptchaToken ? { 'X-Recaptcha-Token': recaptchaToken } : {}),
        },
        body: JSON.stringify({
          repair_type: form.repair_type,
          device_brand: form.device_brand,
          device_brand_other: form.device_brand === 'Otro' ? form.device_brand_other : undefined,
          device_model: form.device_model || undefined,
          device_serial: form.device_serial || undefined,
          problem_description: form.problem_description || undefined,
          customer_name: form.customer_name,
          customer_email: form.customer_email,
          customer_phone: form.customer_phone || undefined,
          customer_nif: form.customer_nif || undefined,
          customer_language: i18n.language,
          pickup_address,
          shipping_option: 'pickup',
          accessories_included: form.accessories.length > 0 ? form.accessories : undefined,
          accessories_notes: form.accessories_notes || undefined,
          consent_data_disclaimer: form.consent_data,
          consent_terms: form.consent_terms,
          estimate_min: estimate?.estimate_min,
          estimate_max: estimate?.estimate_max,
          estimated_days_min: estimate?.days_min,
          estimated_days_max: estimate?.days_max,
          session_id: sessionId,
          source,
          promo_code: promoValid?.code || undefined,
        }),
      })

      const data = await res.json()
      if (!res.ok) throw new Error(data.detail || 'Error')

      // Upload photos
      if (photos.length > 0 && data.code) {
        const formData = new FormData()
        formData.append('photo_type', 'device')
        photos.forEach(f => formData.append('files', f))
        await fetch(`${API_BASE}/repairs/${data.code}/photos`, {
          method: 'POST',
          body: formData,
        })
      }

      setResult(data)
      setStep(4)
    } catch (err: any) {
      alert(err.message || t('repairs.nav.submitError'))
    } finally {
      setLoading(false)
    }
  }

  // WhatsApp message
  const whatsappMessage = useMemo(() => {
    const typeName = form.repair_type ? t(`repairs.types.${form.repair_type}.name`) : ''
    const brandName = form.device_brand && form.device_brand !== 'Otro' ? form.device_brand : ''
    if (typeName && brandName) return t('repairs.whatsapp.messageWithType', { type: typeName, brand: brandName })
    if (typeName) return t('repairs.whatsapp.messageWithTypeOnly', { type: typeName })
    return t('repairs.whatsapp.defaultMessage')
  }, [form.repair_type, form.device_brand, t])

  const totalSteps = 3

  const canNext = () => {
    switch (step) {
      case 1: return !!form.repair_type && !!form.device_brand && (form.device_brand !== 'Otro' || !!form.device_brand_other)
      case 2: {
        const base = !!form.customer_name && !!form.customer_email && !!form.customer_phone && !!form.street && !!form.postal_code && !!form.city
        if (!base) return false
        const cp = form.postal_code.trim()
        if (form.country_code === 'PT') return /^\d{4}-?\d{3}$/.test(cp)
        return /^\d{5}$/.test(cp)
      }
      case 3: return form.consent_data && form.consent_terms && form.consent_diagnostic && form.consent_packaging
      default: return false
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/jvs-logo.jpg" alt="JVS Informatica" className="h-14" />
              <div className="hidden sm:block border-l border-slate-200 pl-3">
                <p className="text-sm font-semibold text-slate-800">{t('repairs.header.title')}</p>
                <p className="text-xs text-slate-400">jvs-informatica.com</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <LanguageSwitcher />
              {customer ? (
                <span className="text-sm text-emerald-600 flex items-center gap-1"><User className="w-3.5 h-3.5" />{customer.firstname}</span>
              ) : (
                <button onClick={() => setShowLogin(true)} className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1">
                  <LogIn className="w-3.5 h-3.5" />{t('repairs.auth.login')}
                </button>
              )}
              <a href="/repairs/track" className="text-sm text-blue-600 hover:text-blue-800 font-medium">{t('repairs.header.trackLink')}</a>
            </div>
          </div>
        </div>
      </header>

      {/* Google Reviews trust badge */}
      {step <= totalSteps && (
        <div className="bg-white border-b border-slate-100">
          <div className="max-w-3xl mx-auto px-4 py-2">
            <a href="https://www.google.com/maps/search/JVS+Informatica+Casar+de+Periedo+Cantabria/"
               target="_blank" rel="noopener noreferrer"
               className="flex items-center justify-center gap-3 hover:opacity-80 transition-opacity">
              <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              <div className="flex items-center gap-1.5">
                <span className="text-2xl font-bold text-slate-800">4.8</span>
                <div className="flex items-center">
                  {[1,2,3,4,5].map(i => (
                    <Star key={i} className={`w-4.5 h-4.5 ${i <= 4 ? 'text-amber-400 fill-amber-400' : 'text-amber-300 fill-amber-200'}`} />
                  ))}
                </div>
              </div>
              <span className="text-sm text-slate-500">{t('repairs.googleReviews.badge')}</span>
            </a>
          </div>
        </div>
      )}

      {/* Login modal */}
      {showLogin && !customer && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-semibold text-slate-900 mb-1">{t('repairs.auth.loginTitle')}</h3>
            <p className="text-sm text-slate-500 mb-4">{t('repairs.auth.loginSubtitle')}</p>
            <div className="space-y-3">
              <input type="email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('repairs.auth.emailPlaceholder')} />
              <input type="password" value={loginPassword} onChange={e => setLoginPassword(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && loginEmail && loginPassword) handleLogin() }}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('repairs.auth.passwordPlaceholder')} />
              {loginError && <p className="text-red-500 text-sm">{loginError}</p>}
              <button onClick={handleLogin} disabled={loginLoading || !loginEmail || !loginPassword}
                className="w-full px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-40 flex items-center justify-center gap-2">
                {loginLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {t('repairs.auth.loginButton')}
              </button>
              <button onClick={() => setShowLogin(false)}
                className="w-full px-4 py-2 text-sm text-slate-500 hover:text-slate-700">
                {t('repairs.auth.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-2xl mx-auto px-4 py-8 flex-1">
        {/* Progress */}
        {step <= totalSteps && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-500">{t('repairs.nav.stepOf', { step, total: totalSteps })}</span>
            </div>
            <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 rounded-full transition-all duration-300" style={{ width: `${(step / totalSteps) * 100}%` }} />
            </div>
          </div>
        )}

        {/* STEP 1: Type + Brand + Model + Estimate + Description + Recent repairs */}
        {step === 1 && (
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">{t('repairs.step1.title')}</h2>
            <p className="text-slate-500 mb-6">{t('repairs.step1.subtitle')}</p>

            {/* How it works */}
            {!form.repair_type && (
              <div className="mb-8 bg-white border border-slate-200 rounded-2xl p-5">
                <h3 className="text-sm font-semibold text-slate-800 mb-4">{t('repairs.howItWorks.title')}</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { icon: ClipboardList, step: '1', key: 'request' },
                    { icon: Truck, step: '2', key: 'pickup' },
                    { icon: Wrench, step: '3', key: 'repair' },
                    { icon: PackageCheck, step: '4', key: 'return' },
                  ].map(s => (
                    <div key={s.step} className="text-center">
                      <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-2">
                        <s.icon className="w-5 h-5 text-blue-600" />
                      </div>
                      <p className="text-xs font-semibold text-slate-800">{t(`repairs.howItWorks.${s.key}Title`)}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{t(`repairs.howItWorks.${s.key}Desc`)}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-3 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {['tracking', customerType !== 'PVP' ? 'noCostPvd' : 'noCostPvp', customerType !== 'PVP' ? 'noCommitPvd' : 'noCommitPvp'].map(k => (
                    <div key={k} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-slate-600">{t(`repairs.howItWorks.${k}`)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Type grid */}
            <div className="grid grid-cols-2 gap-3">
              {REPAIR_TYPE_CODES.map(type => {
                const Icon = type.icon
                const selected = form.repair_type === type.code
                return (
                  <button key={type.code} onClick={() => update('repair_type', type.code)}
                    className={`flex items-start gap-3 p-4 rounded-xl border-2 transition-all text-left ${selected ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' : 'border-slate-200 bg-white hover:border-slate-300'}`}>
                    <div className={`p-2 rounded-lg ${selected ? 'bg-blue-100' : 'bg-slate-100'}`}>
                      <Icon className={`w-5 h-5 ${selected ? 'text-blue-600' : 'text-slate-500'}`} />
                    </div>
                    <div>
                      <p className={`font-medium ${selected ? 'text-blue-700' : 'text-slate-900'}`}>{t(`repairs.types.${type.code}.name`)}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{t(`repairs.types.${type.code}.desc`)}</p>
                    </div>
                  </button>
                )
              })}
            </div>

            {/* Brand grid - appears when type is selected */}
            {form.repair_type && (
              <div className="mt-6">
                <label className="block text-sm font-medium text-slate-700 mb-2">{t('repairs.step2.brand')}</label>
                <div className="grid grid-cols-3 gap-2">
                  {[...BRANDS, 'Otro'].map(b => (
                    <button key={b} onClick={() => update('device_brand', b)}
                      className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all ${form.device_brand === b ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'}`}>
                      {b === 'Otro' ? t('repairs.brands.other') : b}
                    </button>
                  ))}
                </div>
                {form.device_brand === 'Otro' && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('repairs.step2.otherBrand')}</label>
                    <input value={form.device_brand_other} onChange={e => update('device_brand_other', e.target.value)}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder={t('repairs.step2.otherBrandPlaceholder')} />
                  </div>
                )}
              </div>
            )}

            {/* Model + Serial - appears when brand is selected */}
            {form.device_brand && (
              <div className="mt-4 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('repairs.step2.model')}</label>
                  <input value={form.device_model} onChange={e => update('device_model', e.target.value)}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder={t('repairs.step2.modelPlaceholder')} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('repairs.step2.serial')} <span className="text-slate-400">{t('repairs.step2.recommended')}</span></label>
                  <input value={form.device_serial} onChange={e => update('device_serial', e.target.value)}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder={t('repairs.step2.serialPlaceholder')} />
                </div>
              </div>
            )}

            {/* Promo code input */}
            {form.repair_type && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('repairs.promo.label')}</label>
                <div className="flex gap-2">
                  <input value={promoCode} onChange={e => setPromoCode(e.target.value.toUpperCase())}
                    onKeyDown={e => { if (e.key === 'Enter') validatePromo(promoCode) }}
                    className={`flex-1 px-4 py-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono tracking-wider ${promoValid ? 'border-emerald-400 bg-emerald-50' : promoError ? 'border-red-300' : 'border-slate-200'}`}
                    placeholder="XXXXXX" />
                  <button onClick={() => validatePromo(promoCode)} disabled={promoChecking || !promoCode.trim()}
                    className="px-4 py-2.5 bg-slate-100 text-slate-700 text-sm font-medium rounded-xl hover:bg-slate-200 disabled:opacity-40">
                    {promoChecking ? <Loader2 className="w-4 h-4 animate-spin" /> : t('repairs.promo.apply')}
                  </button>
                </div>
                {promoValid && (
                  <div className="mt-2 flex items-center gap-2 text-sm text-emerald-600">
                    <Check className="w-4 h-4" />
                    <span className="font-medium">{promoValid.code}</span> — {promoValid.description}
                  </div>
                )}
                {promoError && <p className="mt-1 text-sm text-red-500">{promoError}</p>}
              </div>
            )}

            {/* Estimate - appears automatically when type is selected */}
            {estimate && form.repair_type && (
              <div className="mt-6 bg-white border border-slate-200 rounded-xl p-6 space-y-4">
                <div className="text-center">
                  <p className="text-sm text-slate-500 mb-1">{t('repairs.step3.estimatedPrice')}</p>
                  {estimate.original_max ? (
                    <>
                      <p className="text-lg text-slate-400 line-through">{estimate.original_min?.toFixed(0)} € – {estimate.original_max?.toFixed(0)} €</p>
                      <p className="text-3xl font-bold text-emerald-600">{estimate.estimate_min?.toFixed(0)} € – {estimate.estimate_max?.toFixed(0)} €</p>
                      <span className="inline-block mt-1 px-3 py-0.5 bg-emerald-100 text-emerald-700 text-sm font-semibold rounded-full">-{estimate.discount_percent}% {t('repairs.promo.withCode')} {estimate.promo_applied}</span>
                    </>
                  ) : (
                    <p className="text-3xl font-bold text-blue-600">{estimate.estimate_min?.toFixed(0)} € – {estimate.estimate_max?.toFixed(0)} €</p>
                  )}
                  <p className="text-sm text-slate-400 mt-1">{t('repairs.step3.estimatedDays', { min: estimate.days_min, max: estimate.days_max })}</p>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <p className="text-sm text-emerald-700 font-medium">{estimate.free_shipping ? t('repairs.promo.freeDiagnosticAndShipping') : t(customerType !== 'PVP' ? 'repairs.step3.freeDiagnosticPvd' : 'repairs.step3.freeDiagnostic')}</p>
                  {!estimate.free_shipping && <p className="text-xs text-emerald-600 mt-1">{t(customerType !== 'PVP' ? 'repairs.step3.diagnosticDescPvd' : 'repairs.step3.diagnosticDesc', { fee: diagnosticFee.toFixed(2) })}</p>}
                </div>
              </div>
            )}

            {/* Problem description */}
            {form.repair_type && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('repairs.step3.describeLabel')}</label>
                <textarea value={form.problem_description} onChange={e => update('problem_description', e.target.value)} rows={3}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t('repairs.step3.describePlaceholder')} />
              </div>
            )}

            {/* Recent completed repairs */}
            {recentRepairs.length > 0 && (
              <div className="mt-6">
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-500" />
                  {t('repairs.recentRepairs.title')}
                </h3>
                <div className="space-y-1.5">
                  {recentRepairs.slice(0, 6).map((r: any, i: number) => (
                    <div key={i} className="flex items-center justify-between p-2.5 bg-white border border-slate-100 rounded-lg text-sm">
                      <span className="text-slate-600 truncate mr-3">
                        {r.description || t(`repairs.types.${r.repair_type}.name`)} — {r.device_brand} {r.device_model_masked}
                      </span>
                      <span className="font-semibold text-emerald-600 whitespace-nowrap">
                        {r.final_price?.toFixed(2)} €{!priceIncludesVat && ' + IVA'}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mt-2">
                  {priceIncludesVat ? t('repairs.recentRepairs.vatIncluded') : t('repairs.recentRepairs.vatNotIncluded')}
                </p>
              </div>
            )}
          </div>
        )}

        {/* STEP 2: Contact + Address */}
        {step === 2 && (
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">{t('repairs.step5.title')}</h2>
            <p className="text-slate-500 mb-6">{t('repairs.step5.subtitle')}</p>

            {customer && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4 text-sm text-emerald-700">
                <User className="w-4 h-4 inline mr-1" />
                {t('repairs.auth.loggedAs', { name: customer.firstname })}
              </div>
            )}

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('repairs.step5.name')} *</label>
                  <input value={form.customer_name} onChange={e => update('customer_name', e.target.value)}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('repairs.step5.email')} *</label>
                  <input type="email" value={form.customer_email} onChange={e => update('customer_email', e.target.value)}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('repairs.step5.phone')} *</label>
                  <input type="tel" value={form.customer_phone} onChange={e => update('customer_phone', e.target.value)}
                    placeholder={t('repairs.step5.phonePlaceholder')}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('repairs.step5.nif')} <span className="text-slate-400">{t('repairs.step5.optional')}</span></label>
                  <input value={form.customer_nif} onChange={e => update('customer_nif', e.target.value)}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div className="border-t border-slate-200 pt-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">{t('repairs.step5.addressTitle')} *</h3>
                <div className="space-y-3">
                  <input value={form.street} onChange={e => update('street', e.target.value)} placeholder={t('repairs.step5.street')}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500" />
                  <div className="grid grid-cols-3 gap-3">
                    <input value={form.postal_code} onChange={e => update('postal_code', e.target.value)}
                      placeholder={form.country_code === 'PT' ? '1000-001' : '28001'}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500" />
                    <input value={form.city} onChange={e => update('city', e.target.value)} placeholder={t('repairs.step5.city')}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500" />
                    <input value={form.province} onChange={e => update('province', e.target.value)} placeholder={t('repairs.step5.province')}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <select value={form.country_code} onChange={e => update('country_code', e.target.value)}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500">
                    <option value="ES">{t('repairs.step5.spain')}</option>
                    <option value="PT">{t('repairs.step5.portugal')}</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* STEP 3: Photos + Accessories + Packaging + Summary + Consents */}
        {step === 3 && (
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">{t('repairs.step3New.title')}</h2>
            <p className="text-slate-500 mb-6">{t('repairs.step3New.subtitle')}</p>

            {/* Photos section */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">{t('repairs.step4.title')}</h3>
              <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center bg-white">
                <Camera className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                <label className="cursor-pointer">
                  <span className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700">{t('repairs.step4.selectPhotos')}</span>
                  <input type="file" multiple accept="image/*" onChange={handlePhotoAdd} className="hidden" />
                </label>
                <p className="text-xs text-slate-400 mt-2">{t('repairs.step4.fileInfo')}</p>
              </div>
              {photos.length > 0 && (
                <div className="grid grid-cols-3 gap-3 mt-4">
                  {photos.map((f, i) => (
                    <div key={i} className="relative group">
                      <img src={URL.createObjectURL(f)} alt="" className="w-full h-24 object-cover rounded-lg border border-slate-200" />
                      <button onClick={() => removePhoto(i)} className="absolute top-1 right-1 p-1 bg-white/90 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                        <X className="w-3 h-3 text-slate-600" />
                      </button>
                      <p className="text-xs text-slate-500 truncate mt-0.5">{f.name}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Accessories */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">{t('repairs.step6.title')}</h3>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                <p className="text-sm text-amber-700">{t('repairs.step6.recommendation')}</p>
              </div>
              <div className="space-y-2">
                {ACCESSORIES.map(acc => (
                  <label key={acc.value} className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-xl cursor-pointer hover:border-slate-300">
                    <input type="checkbox" checked={form.accessories.includes(acc.value)}
                      onChange={e => {
                        if (e.target.checked) update('accessories', [...form.accessories, acc.value])
                        else update('accessories', form.accessories.filter(a => a !== acc.value))
                      }}
                      className="rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                    <span className="text-sm text-slate-700">{t(acc.key)}</span>
                  </label>
                ))}
              </div>
              <div className="mt-3">
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('repairs.step6.otherLabel')}</label>
                <input value={form.accessories_notes} onChange={e => update('accessories_notes', e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm" placeholder={t('repairs.step6.otherPlaceholder')} />
              </div>
            </div>

            {/* Packaging warning */}
            <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <PackageCheck className="w-5 h-5 text-red-600" />
                <h3 className="font-semibold text-red-800">{t('repairs.step6.packagingTitle')}</h3>
              </div>
              <p className="text-sm text-red-700 mb-3" dangerouslySetInnerHTML={{ __html: t('repairs.step6.packagingWarning') }} />
              <ul className="space-y-1.5 mb-3">
                <li className="flex items-start gap-2 text-sm text-red-700">
                  <Check className="w-4 h-4 mt-0.5 text-red-500 flex-shrink-0" />
                  {t('repairs.step6.packagingTip1')}
                </li>
                <li className="flex items-start gap-2 text-sm text-red-700">
                  <Check className="w-4 h-4 mt-0.5 text-red-500 flex-shrink-0" />
                  {t('repairs.step6.packagingTip2')}
                </li>
                <li className="flex items-start gap-2 text-sm text-red-700">
                  <Check className="w-4 h-4 mt-0.5 text-red-500 flex-shrink-0" />
                  {t('repairs.step6.packagingTip3')}
                </li>
              </ul>
              <div className="flex items-start gap-2 bg-red-100 rounded-lg p-3">
                <ShieldAlert className="w-4 h-4 mt-0.5 text-red-600 flex-shrink-0" />
                <p className="text-xs text-red-800 font-medium" dangerouslySetInnerHTML={{ __html: t('repairs.step6.packagingDisclaimer') }} />
              </div>
            </div>

            {/* Summary card */}
            <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">{t('repairs.step7.type')}</span><span className="font-medium">{t(`repairs.types.${form.repair_type}.name`)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">{t('repairs.step7.device')}</span><span className="font-medium">{form.device_brand === 'Otro' ? form.device_brand_other : form.device_brand} {form.device_model}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">{t('repairs.step7.client')}</span><span className="font-medium">{form.customer_name}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">{t('repairs.step7.email')}</span><span className="font-medium">{form.customer_email}</span></div>
              {estimate && <div className="flex justify-between"><span className="text-slate-500">{t('repairs.step7.estimate')}</span><span className="font-medium text-blue-600">{estimate.estimate_min?.toFixed(0)} – {estimate.estimate_max?.toFixed(0)} €</span></div>}
              <div className="flex justify-between"><span className="text-slate-500">{t('repairs.step7.photos')}</span><span className="font-medium">{photos.length} {t('repairs.step7.files')}</span></div>
            </div>

            {/* Consents */}
            <div className="space-y-3">
              <label className="flex items-start gap-3 p-3 bg-white border border-slate-200 rounded-xl cursor-pointer hover:border-slate-300">
                <input type="checkbox" checked={form.consent_terms} onChange={e => update('consent_terms', e.target.checked)}
                  className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 mt-0.5" />
                <span className="text-sm text-slate-700">{t('repairs.step7.consentTerms')}</span>
              </label>
              <label className="flex items-start gap-3 p-3 bg-white border border-slate-200 rounded-xl cursor-pointer hover:border-slate-300">
                <input type="checkbox" checked={form.consent_data} onChange={e => update('consent_data', e.target.checked)}
                  className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 mt-0.5" />
                <span className="text-sm text-slate-700" dangerouslySetInnerHTML={{ __html: t('repairs.step7.consentData') }} />
              </label>
              <label className="flex items-start gap-3 p-3 bg-white border border-slate-200 rounded-xl cursor-pointer hover:border-slate-300">
                <input type="checkbox" checked={form.consent_diagnostic} onChange={e => update('consent_diagnostic', e.target.checked)}
                  className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 mt-0.5" />
                <span className="text-sm text-slate-700" dangerouslySetInnerHTML={{ __html: t(customerType !== 'PVP' ? 'repairs.step7.consentDiagnosticPvd' : 'repairs.step7.consentDiagnostic', { fee: diagnosticFee.toFixed(2) }) }} />
              </label>
              <label className="flex items-start gap-3 p-3 bg-white border border-red-200 rounded-xl cursor-pointer hover:border-red-300">
                <input type="checkbox" checked={form.consent_packaging} onChange={e => update('consent_packaging', e.target.checked)}
                  className="rounded border-red-300 text-red-600 focus:ring-red-500 mt-0.5" />
                <span className="text-sm text-slate-700" dangerouslySetInnerHTML={{ __html: t('repairs.step7.consentPackaging') }} />
              </label>
            </div>
          </div>
        )}

        {/* STEP 4: Success */}
        {step === 4 && result && (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">{t('repairs.step8.title')}</h2>
            <p className="text-slate-500 mb-6">{t('repairs.step8.subtitle')}</p>
            <div className="inline-block bg-blue-50 border-2 border-blue-200 rounded-xl px-8 py-4 mb-6">
              <p className="text-3xl font-mono font-bold text-blue-600">{result.code}</p>
            </div>
            <p className="text-sm text-slate-500 mb-6">{t('repairs.step8.emailInfo')}</p>
            <div className="flex items-center justify-center gap-3">
              <a href={`/repairs/track/${result.code}`}
                className="px-6 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700">
                {t('repairs.step8.trackLink')}
              </a>
            </div>
          </div>
        )}

        {/* Navigation */}
        {step <= totalSteps && (
          <div className="flex items-center justify-between mt-8">
            <button onClick={() => setStep(s => Math.max(1, s - 1))} disabled={step === 1}
              className="flex items-center gap-2 px-4 py-2 text-sm text-slate-600 hover:text-slate-800 disabled:opacity-40">
              <ChevronLeft className="w-4 h-4" />{t('repairs.nav.previous')}
            </button>
            {step < totalSteps ? (
              <button onClick={() => setStep(s => s + 1)} disabled={!canNext()}
                className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-40">
                {t('repairs.nav.next')}<ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button onClick={submit} disabled={loading || !canNext()}
                className="flex items-center gap-2 px-6 py-2.5 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-40">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {t('repairs.nav.submit')}
              </button>
            )}
          </div>
        )}
      </main>

      {/* WhatsApp floating button */}
      {step <= totalSteps && (
        <a href={`https://wa.me/34644464960?text=${encodeURIComponent(whatsappMessage)}`}
           target="_blank" rel="noopener noreferrer"
           className="fixed bottom-6 right-6 w-14 h-14 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center shadow-lg z-40 transition-transform hover:scale-110"
           title={t('repairs.whatsapp.tooltip')}>
          <MessageCircle className="w-7 h-7 text-white fill-white" />
        </a>
      )}

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 mt-auto">
        <div className="max-w-2xl mx-auto px-4 py-5">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-500">
            <div className="flex items-center gap-2">
              <img src="/jvs-logo.jpg" alt="JVS" className="h-9 opacity-70" />
              <span>Especialistas en portatiles</span>
            </div>
            <div className="flex items-center gap-4">
              <a href="tel:+34942719735" className="flex items-center gap-1 hover:text-slate-700"><Phone className="w-3.5 h-3.5" />942 71 97 35</a>
              <a href="https://wa.me/34644464960" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-green-600"><MessageCircle className="w-3.5 h-3.5" />644 46 49 60</a>
              <a href="https://jvs-informatica.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-slate-700"><Globe className="w-3.5 h-3.5" />jvs-informatica.com</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
